/**
 * VideoCreator - Modal for creating videos from analyzed tracks
 *
 * Flow:
 * 1. Load trim recommendations
 * 2. User selects trim duration and adjusts mood
 * 3. User picks quality tier (draft/standard/premium)
 * 4. Preview estimated cost
 * 5. Start generation
 * 6. Show progress with dancing octopus
 * 7. Show completion with video preview
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { X, Video, Sparkles, DollarSign, AlertTriangle, ExternalLink, Download, Clock, Zap, Layers, CheckCircle2, Loader2, Info, Brain, Eye /* GitCompare */ } from 'lucide-react';
import { useBrainPipeline } from '../../hooks/useBrainPipeline';
import BrainPipelineProgress from './BrainPipelineProgress';

// Module-level guards that persist across component remounts
// These prevent infinite loops when component is unmounted/remounted rapidly
const fetchGuards = {
  trackId: null as string | null,
  trimsId: null as string | null,
  inProgress: {
    track: false,
    trims: false,
  },
};
import Button from '../common/Button';
import ClassificationFeedback from './ClassificationFeedback';
import DryRunPreview from './DryRunPreview';
import OctopusLoader from '../common/OctopusLoader';
import EmotionalMixer from './EmotionalMixer';
import VideoPreviewPlayer from './VideoPreviewPlayer';
import VideoComparison from './VideoComparison';
import CulturalSelector from './CulturalSelector';
import { api } from '../../services/api';
import { useTrackCache } from '../../contexts/TrackCacheContext';
import type { TrackContext } from '../../types';

interface TrimRecommendation {
  duration: number;
  start_time: number;
  end_time: number;
  score: number;
  rationale: string;
  includes_peak: boolean;
  hook_energy: number;
}

interface TrimSelection {
  start_time: number;
  end_time: number;
  duration: number;
}

interface EmotionalAdjustments {
  mood_bias: Record<string, number>;
  color_temperature: number;
  motion_intensity: number;
}

interface VideoCreatorProps {
  isOpen: boolean;
  onClose: () => void;
  track: TrackContext;
  personaId: string;
  onComplete?: (videoUrl: string) => void;
  initialCulturalContext?: string | null;
}

/**
 * Get a clean display name for a track, hiding UUIDs and timestamps
 */
function getTrackDisplayName(track: TrackContext): string {
  // Prefer display_name if set
  if (track.display_name) {
    return track.display_name;
  }

  // Clean up filename: format is usually "{persona_id}/tracks/{timestamp}-{uuid}-{original_filename}"
  if (track.filename) {
    // If filename contains the pattern timestamp-uuid-name, extract just the name
    const parts = track.filename.split('-');
    if (parts.length >= 3) {
      // Skip timestamp and uuid prefix parts, join the rest
      return parts.slice(2).join('-').replace(/_/g, ' ').replace(/\.[^/.]+$/, ''); // Remove extension too
    }
    // Otherwise just clean up the filename
    return track.filename.replace(/_/g, ' ').replace(/\.[^/.]+$/, '');
  }

  return 'Untitled Track';
}

type QualityTier = 'draft' | 'standard' | 'premium';

interface QualityOption {
  tier: QualityTier;
  name: string;
  description: string;
  providers: string[];
  strategy: string;
  costPerSecond: number;
  estimatedTime: string;
}

const QUALITY_OPTIONS: QualityOption[] = [
  {
    tier: 'draft',
    name: 'Draft',
    description: 'Fast preview, stylized visuals',
    providers: ['Mochi', 'ZeroScope', 'AnimateDiff'],
    strategy: 'Waterfall (fastest first)',
    costPerSecond: 0.0,
    estimatedTime: '1-2 min',
  },
  {
    tier: 'standard',
    name: 'Standard',
    description: 'Balanced quality & speed',
    providers: ['Hailuo', 'Luma', 'Stability SVD'],
    strategy: 'Hybrid (race cheap, fallback premium)',
    costPerSecond: 0.025,
    estimatedTime: '2-4 min',
  },
  {
    tier: 'premium',
    name: 'Premium',
    description: 'Best quality, cinematic',
    providers: ['Kling', 'Luma Dream Machine', 'Hailuo'],
    strategy: 'Race (parallel, use fastest)',
    costPerSecond: 0.045,
    estimatedTime: '3-5 min',
  },
];

type Stage = 'setup' | 'previewing' | 'preview' | 'generating' | 'complete' | 'error';

export default function VideoCreator({
  isOpen,
  onClose,
  track,
  personaId,
  onComplete,
  initialCulturalContext,
}: VideoCreatorProps) {
  // Use track cache to fetch full track data
  const { getTrack, fetchTrack, setTrack: cacheTrack } = useTrackCache();

  // State for full track data (with context_data including lyrics, insights, etc.)
  const [fullTrack, setFullTrack] = useState<TrackContext | null>(null);
  const [isLoadingTrack, setIsLoadingTrack] = useState(true);

  // Brain pipeline hook
  const brainPipeline = useBrainPipeline();

  // State
  const [stage, setStage] = useState<Stage>('setup');
  const [useBrain, setUseBrain] = useState(true); // Enable brain-powered generation by default
  const [isLoadingTrims, setIsLoadingTrims] = useState(true);
  const [trimRecommendations, setTrimRecommendations] = useState<TrimRecommendation[]>([]);
  const [selectedTrim, setSelectedTrim] = useState<TrimSelection | undefined>();
  const [adjustments, setAdjustments] = useState<EmotionalAdjustments>({
    mood_bias: {},
    color_temperature: 0,
    motion_intensity: 0.5,
  });
  const [qualityTier, setQualityTier] = useState<QualityTier>('standard');
  const [culturalContext, setCulturalContext] = useState<string | null>(initialCulturalContext || null);
  const [error, setError] = useState<string | null>(null);

  // Generation results
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [sceneResults, setSceneResults] = useState<Array<{
    scene_id: number;
    status: string;
    video_url: string | null;
    cost_usd: number;
  }>>([]);
  const [totalCost, setTotalCost] = useState(0);
  const [processingTime, setProcessingTime] = useState(0);

  // Dry-run preview report
  const [dryRunReport, setDryRunReport] = useState<Record<string, unknown> | null>(null);

  // A/B comparison state
  const [abMode] = useState(false);
  const [optionA] = useState<{ pathway: string; success: boolean; video_url: string | null; provider: string; error?: string | null } | null>(null);
  const [optionB] = useState<{ pathway: string; success: boolean; video_url: string | null; provider: string; error?: string | null } | null>(null);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  // Live progress tracking
  const [elapsedTime, setElapsedTime] = useState(0);
  const [currentStatus, setCurrentStatus] = useState('Initializing...');
  const [sceneProgress, setSceneProgress] = useState<Array<{
    scene_id: number;
    status: 'pending' | 'generating' | 'complete' | 'failed';
    provider?: string;
  }>>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);

  // Define loadTrimRecommendations BEFORE the useEffect that uses it
  const loadTrimRecommendations = useCallback(async () => {
    // Module-level guard to prevent concurrent/repeated fetches
    if (fetchGuards.inProgress.trims || fetchGuards.trimsId === track.id) {
      console.log('[VideoCreator] BLOCKED trim fetch - already fetched or in progress');
      return;
    }

    fetchGuards.inProgress.trims = true;
    fetchGuards.trimsId = track.id;
    setIsLoadingTrims(true);
    setError(null);

    try {
      console.log('[VideoCreator] Loading trim recommendations for:', track.id);
      const response = await fetch(
        `${import.meta.env.VITE_BACKBONE_URL || 'http://localhost:8080'}/api/trim-recommendations`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ track_context_id: track.id }),
        }
      );

      const data = await response.json();

      if (data.success && data.recommendations) {
        console.log('[VideoCreator] Trim recommendations loaded:', data.recommendations.length);
        setTrimRecommendations(data.recommendations);

        // Auto-select 30s recommendation
        const rec30 = data.recommendations.find((r: TrimRecommendation) => r.duration === 30);
        if (rec30) {
          setSelectedTrim({
            start_time: rec30.start_time,
            end_time: rec30.end_time,
            duration: 30,
          });
        }
      }
    } catch (err) {
      console.error('Failed to load trim recommendations:', err);
      // DON'T reset guard on error - prevents infinite retry loop
    } finally {
      fetchGuards.inProgress.trims = false;
      setIsLoadingTrims(false);
    }
  }, [track.id]);

  // Fetch full track data on mount (includes context_data with lyrics, insights, etc.)
  useEffect(() => {
    // Module-level guard to prevent concurrent/repeated fetches across remounts
    if (!isOpen || !track.id) {
      return;
    }

    if (fetchGuards.inProgress.track || fetchGuards.trackId === track.id) {
      // Check cache for stored data instead of blocking
      const cachedTrack = getTrack(track.id);
      if (cachedTrack) {
        console.log('[VideoCreator] Returning stored track data from cache:', track.id);
        setFullTrack(cachedTrack);
        setIsLoadingTrack(false);
        return;
      }
      // Fallback to basic track prop if cache miss
      if (!fullTrack) {
        console.log('[VideoCreator] Using basic track data (no cache hit)');
        setFullTrack(track);
        setIsLoadingTrack(false);
      }
      return;
    }

    const loadFullTrack = async () => {
      fetchGuards.inProgress.track = true;
      fetchGuards.trackId = track.id;
      setIsLoadingTrack(true);
      console.log('[VideoCreator] Fetching full track data for:', track.id);

      try {
        const fullData = await fetchTrack(track.id);
        if (fullData) {
          console.log('[VideoCreator] Full track data loaded:', {
            id: fullData.id,
            hasContextData: !!fullData.context_data,
            contextDataKeys: fullData.context_data ? Object.keys(fullData.context_data) : [],
          });
          setFullTrack(fullData);
          cacheTrack(fullData); // Update cache
        } else {
          console.warn('[VideoCreator] Failed to fetch full track data, using prop');
          setFullTrack(track);
        }
      } catch (err) {
        console.error('[VideoCreator] Error fetching full track:', err);
        setFullTrack(track);
        // DON'T reset guard on error - prevents infinite retry loop
      } finally {
        fetchGuards.inProgress.track = false;
        setIsLoadingTrack(false);
      }
    };
    loadFullTrack();
  }, [isOpen, track, fetchTrack, cacheTrack, fullTrack]);

  // Load trim recommendations after full track is loaded
  useEffect(() => {
    if (!isOpen || !fullTrack?.id || isLoadingTrack) {
      return;
    }
    loadTrimRecommendations();
  }, [isOpen, fullTrack?.id, isLoadingTrack, loadTrimRecommendations]);

  // Poll for enhancement status updates when status is 'enhancing'
  useEffect(() => {
    if (!isOpen || !fullTrack?.id) return;

    const enhancementStatus = fullTrack.enhancement_status;
    // Only poll if status is 'enhancing' or 'pending' (deep analysis might still start)
    if (enhancementStatus !== 'enhancing' && enhancementStatus !== 'pending') {
      return;
    }

    console.log('[VideoCreator] Starting enhancement status polling for:', fullTrack.id);

    const pollInterval = setInterval(async () => {
      try {
        // Use forceRefresh=true to bypass cache and get fresh data from API
        const updatedTrack = await fetchTrack(fullTrack.id, true);
        if (updatedTrack) {
          const newStatus = updatedTrack.enhancement_status;
          console.log('[VideoCreator] Enhancement status poll:', newStatus);

          if (newStatus !== enhancementStatus) {
            console.log('[VideoCreator] Enhancement status changed:', enhancementStatus, '->', newStatus);
            setFullTrack(updatedTrack);
            cacheTrack(updatedTrack);

            // Stop polling if no longer enhancing
            if (newStatus !== 'enhancing' && newStatus !== 'pending') {
              clearInterval(pollInterval);
            }
          }
        }
      } catch (err) {
        console.error('[VideoCreator] Enhancement poll error:', err);
      }
    }, 3000); // Poll every 3 seconds

    return () => {
      console.log('[VideoCreator] Stopping enhancement status polling');
      clearInterval(pollInterval);
    };
  }, [isOpen, fullTrack?.id, fullTrack?.enhancement_status, fetchTrack, cacheTrack]);

  // Reset module-level guards when modal closes (allows fresh fetch on reopen)
  useEffect(() => {
    if (!isOpen) {
      console.log('[VideoCreator] Modal closed, resetting fetch guards');
      fetchGuards.trackId = null;
      fetchGuards.trimsId = null;
      fetchGuards.inProgress.track = false;
      fetchGuards.inProgress.trims = false;
    }
  }, [isOpen]);

  // Timer for generation progress
  useEffect(() => {
    if (stage === 'generating') {
      startTimeRef.current = Date.now();
      timerRef.current = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [stage]);

  const estimatedCost = useCallback(() => {
    if (!selectedTrim) return 0;
    const option = QUALITY_OPTIONS.find((o) => o.tier === qualityTier);
    if (!option) return 0;

    // Estimate ~8 scenes for 30s video, scale proportionally
    const sceneCount = Math.ceil((selectedTrim.duration / 30) * 8);
    const avgSceneDuration = selectedTrim.duration / sceneCount;

    return sceneCount * avgSceneDuration * option.costPerSecond;
  }, [selectedTrim, qualityTier]);

  // Record video engagement signals for the learning module
  const recordEngagement = async (opts: {
    modelId?: string;
    wasRegenerated?: boolean;
    wasSelected?: boolean;
    userRating?: number;
  } = {}) => {
    try {
      const brief = brainPipeline.productionBrief?.brief || {};
      const musicDna = brief.music_dna as Record<string, unknown> | undefined;
      const genre = (musicDna?.genre as string) || (fullTrack || track)?.primary_genre || '';
      const moodTags = (musicDna?.mood_tags as string[]) || [];
      const backboneUrl = import.meta.env.VITE_BACKBONE_URL || 'http://localhost:8080';

      await fetch(`${backboneUrl}/api/video-engagement`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: track.id,
          persona_id: personaId,
          model_id: opts.modelId || 'unknown',
          mood_tags: moodTags,
          genre,
          was_regenerated: opts.wasRegenerated || false,
          was_selected: opts.wasSelected !== false,
          user_rating: opts.userRating,
          time_to_approve_ms: processingTime || undefined,
        }),
      });
    } catch (err) {
      console.warn('[VideoCreator] Failed to record engagement:', err);
    }
  };

  const handlePreview = async () => {
    if (!selectedTrim) {
      setError('Please select a trim duration');
      return;
    }

    setStage('previewing');
    setError(null);
    setDryRunReport(null);

    try {
      // Create a temporary project for the preview
      const projectResponse = await api.createProject({
        persona_id: personaId,
        track_context_id: track.id,
        target_platform: 'tiktok',
        target_duration: selectedTrim.duration,
        quality_tier: qualityTier,
      });

      if (projectResponse.error || !projectResponse.data?.project) {
        throw new Error(projectResponse.error || 'Failed to create project');
      }

      const projectId = projectResponse.data.project.id;
      const trackData = fullTrack || track;
      const contextData = trackData.context_data || {};

      console.log('[VideoCreator] Starting dry-run preview for:', projectId);

      const response = await fetch(
        `${import.meta.env.VITE_BACKBONE_URL || 'http://localhost:8080'}/api/generate-video`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            project_id: projectId,
            persona_id: personaId,
            track_context_id: trackData.id,
            trim_selection: selectedTrim,
            emotional_adjustments: adjustments,
            quality_tier: qualityTier,
            target_platform: 'tiktok',
            cultural_context: culturalContext,
            track_context_data: contextData,
            use_brain: useBrain,
            dry_run: true,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || result.error || `Server error (${response.status})`);
      }

      if (result.dry_run_report) {
        setDryRunReport(result.dry_run_report);
        setStage('preview');
      } else {
        // Log the full response for debugging
        console.error('[VideoCreator] Unexpected response:', JSON.stringify(result).slice(0, 500));
        throw new Error(result.error || `Preview returned status "${result.status}" with no report`);
      }
    } catch (err) {
      console.error('Dry-run preview error:', err);
      setError(err instanceof Error ? err.message : 'Preview failed');
      setStage('error');
    }
  };

  const handleStartGeneration = async () => {
    if (!selectedTrim) {
      setError('Please select a trim duration');
      return;
    }

    // Reset progress state
    setStage('generating');
    setError(null);
    setElapsedTime(0);
    setCurrentStatus('Creating video project...');
    setSceneProgress([]);

    // Reset brain pipeline if using it
    if (useBrain) {
      brainPipeline.reset();
    }

    try {
      // First create a video project
      setCurrentStatus('Setting up video project...');
      const projectResponse = await api.createProject({
        persona_id: personaId,
        track_context_id: track.id,
        target_platform: 'tiktok',
        target_duration: selectedTrim.duration,
        quality_tier: qualityTier,
      });

      if (projectResponse.error || !projectResponse.data?.project) {
        throw new Error(projectResponse.error || 'Failed to create project');
      }

      const projectId = projectResponse.data.project.id;

      // Use fullTrack for context data (includes lyrics, insights, etc.)
      const trackData = fullTrack || track;
      const contextData = trackData.context_data || {};

      console.log('[VideoCreator] Starting generation with context:', {
        track_id: trackData.id,
        has_context_data: !!trackData.context_data,
        context_keys: Object.keys(contextData),
        has_transcription: !!(contextData as { transcription?: unknown }).transcription,
        has_analysis: !!(contextData as { analysis?: unknown }).analysis,
        use_brain: useBrain,
      });

      if (useBrain) {
        // Connect to brain WebSocket for real-time updates
        setCurrentStatus('Connecting to AI brain...');
        await brainPipeline.connect(projectId);
      } else {
        setCurrentStatus('Analyzing track and composing scenes...');
      }

      // Start video generation with EventSource for progress updates
      const generateResponse = await fetch(
        `${import.meta.env.VITE_BACKBONE_URL || 'http://localhost:8080'}/api/generate-video`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            project_id: projectId,
            persona_id: personaId,
            track_context_id: trackData.id,
            trim_selection: selectedTrim,
            emotional_adjustments: adjustments,
            quality_tier: qualityTier,
            target_platform: 'tiktok',
            cultural_context: culturalContext,
            // Pass full context data including lyrics, insights, etc.
            track_context_data: contextData,
            // Enable brain-powered generation
            use_brain: useBrain,
          }),
        }
      );

      const result = await generateResponse.json();

      // Process progress updates from result
      if (result.progress_updates) {
        for (const update of result.progress_updates) {
          if (update.type === 'status') {
            setCurrentStatus(update.message);
          } else if (update.type === 'scene_progress') {
            setSceneProgress(prev => {
              const existing = prev.find(s => s.scene_id === update.scene_id);
              if (existing) {
                return prev.map(s => s.scene_id === update.scene_id ? { ...s, ...update } : s);
              }
              return [...prev, update];
            });
          }
        }
      }

      if (result.success) {
        setVideoUrl(result.video_url);
        setSceneResults(result.scene_results || []);
        setTotalCost(result.total_cost_usd || 0);
        setProcessingTime(result.processing_time_ms || 0);
        setStage('complete');

        // Record engagement signal for the learning module
        recordEngagement({ wasRegenerated: false, wasSelected: true });

        if (onComplete && result.video_url) {
          onComplete(result.video_url);
        }
      } else {
        throw new Error(result.error || 'Video generation failed');
      }
    } catch (err) {
      console.error('Video generation error:', err);
      setError(err instanceof Error ? err.message : 'Generation failed');
      setStage('error');
      // Disconnect brain pipeline on error
      if (useBrain) {
        brainPipeline.disconnect();
      }
    }
  };

  const handleSelectWinner = async (option: 'a' | 'b') => {
    setSelectedOption(option);
    // Save selection to DB via backbone
    try {
      // The option_a/option_b URLs are already stored in video_projects by the backend
      // Just update selected_option
      console.log(`[VideoCreator] Selected winner: option ${option}`);
    } catch (err) {
      console.error('Failed to save winner selection:', err);
    }
  };

  const handleClose = () => {
    if (stage === 'generating' || stage === 'previewing') {
      // Don't allow closing during generation or previewing
      return;
    }
    // Disconnect brain pipeline if connected
    brainPipeline.reset();
    setStage('setup');
    setError(null);
    setVideoUrl(null);
    setSceneResults([]);
    onClose();
  };

  const handleRetry = () => {
    setStage('setup');
    setError(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={stage !== 'generating' ? handleClose : undefined}
      />

      {/* Modal */}
      <div className="relative bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-2xl max-h-[90vh] mx-4 shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <Video className="w-5 h-5 text-jelly-400" />
            <h2 className="text-lg font-semibold">
              {stage === 'setup' && 'Create Video'}
              {stage === 'previewing' && 'Running Preview...'}
              {stage === 'preview' && 'Preview Report'}
              {stage === 'generating' && 'Generating Video'}
              {stage === 'complete' && 'Video Ready'}
              {stage === 'error' && 'Generation Failed'}
            </h2>
          </div>
          {stage !== 'generating' && stage !== 'previewing' && (
            <button
              onClick={handleClose}
              className="p-1 hover:bg-zinc-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Setup Stage */}
          {stage === 'setup' && (
            <div className="space-y-6">
              {/* Loading full track data */}
              {isLoadingTrack ? (
                <OctopusLoader
                  message="Loading track data"
                  rotatingMessages={[
                    "Fetching your track's analysis...",
                    "Loading lyrics and insights...",
                    "Gathering emotional data...",
                  ]}
                />
              ) : fullTrack ? (
                <>
              {/* Track info */}
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <h3 className="font-medium mb-2">{getTrackDisplayName(fullTrack)}</h3>
                <div className="flex gap-4 text-sm text-zinc-400">
                  {fullTrack.primary_genre && <span>{fullTrack.primary_genre}</span>}
                  {fullTrack.tempo_bpm && <span>{Math.round(fullTrack.tempo_bpm)} BPM</span>}
                  {fullTrack.duration_seconds && (
                    <span>
                      {Math.floor(fullTrack.duration_seconds / 60)}:
                      {String(Math.floor(fullTrack.duration_seconds % 60)).padStart(2, '0')}
                    </span>
                  )}
                </div>

                {/* Enhancement Status Indicator */}
                {(() => {
                  const enhancementStatus = fullTrack.enhancement_status;
                  const timeline = fullTrack.context_data?.timeline as { beats?: unknown[]; section_map?: unknown[]; mood_timeline?: unknown[] } | undefined;
                  const hasBeats = timeline?.beats && (timeline.beats as unknown[]).length > 0;
                  const hasSections = timeline?.section_map && (timeline.section_map as unknown[]).length > 0;
                  const hasEnhancedData = hasBeats || hasSections;

                  if (enhancementStatus === 'enhanced' || hasEnhancedData) {
                    return (
                      <div className="mt-3 pt-3 border-t border-zinc-700">
                        <div className="flex items-center gap-2 text-green-400">
                          <CheckCircle2 className="w-4 h-4" />
                          <span className="text-xs font-medium">Deep Analysis Complete</span>
                        </div>
                        <p className="text-xs text-zinc-500 mt-1">
                          Beat sync, sections, and mood timeline ready for optimal scene composition
                        </p>
                      </div>
                    );
                  } else if (enhancementStatus === 'enhancing') {
                    return (
                      <div className="mt-3 pt-3 border-t border-zinc-700">
                        <div className="flex items-center gap-2 text-jelly-400">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-xs font-medium">Deep Analysis in Progress</span>
                        </div>
                        <p className="text-xs text-zinc-500 mt-1">
                          Analyzing beats, sections, and mood for better scene transitions...
                        </p>
                      </div>
                    );
                  } else if (enhancementStatus === 'pending' || !enhancementStatus) {
                    return (
                      <div className="mt-3 pt-3 border-t border-zinc-700">
                        <div className="flex items-center gap-2 text-amber-400">
                          <Info className="w-4 h-4" />
                          <span className="text-xs font-medium">Basic Analysis Mode</span>
                        </div>
                        <p className="text-xs text-zinc-500 mt-1">
                          Video will use basic analysis. Deep analysis runs in background for future videos.
                        </p>
                      </div>
                    );
                  } else if (enhancementStatus === 'failed') {
                    return (
                      <div className="mt-3 pt-3 border-t border-zinc-700">
                        <div className="flex items-center gap-2 text-zinc-500">
                          <AlertTriangle className="w-4 h-4" />
                          <span className="text-xs font-medium">Deep Analysis Unavailable</span>
                        </div>
                        <p className="text-xs text-zinc-500 mt-1">
                          Video will use basic analysis data.
                        </p>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Show lyrics preview if available */}
                {fullTrack.context_data && (fullTrack.context_data as { transcription?: { text?: string } }).transcription?.text && (
                  <div className="mt-3 pt-3 border-t border-zinc-700">
                    <p className="text-xs text-zinc-500 mb-1">Lyrics detected</p>
                    <p className="text-xs text-zinc-400 line-clamp-2">
                      {(fullTrack.context_data as { transcription?: { text?: string } }).transcription?.text?.slice(0, 150)}...
                    </p>
                  </div>
                )}
              </div>

              {/* Emotional Mixer */}
              {isLoadingTrims ? (
                <OctopusLoader
                  message="Analyzing your track"
                  rotatingMessages={[
                    "Finding the perfect hook...",
                    "Mapping the emotional peaks...",
                    "Discovering the best moments...",
                    "Feeling the energy curves...",
                    "Catching the vibe...",
                    "Scanning for magic moments...",
                    "Detecting the drop...",
                    "Measuring the feels...",
                    "Tracing the mood arc...",
                    "Locating peak excitement...",
                    "Analyzing the groove...",
                    "Reading the sonic tea leaves...",
                    "Calculating optimal vibes...",
                    "Hunting for the hook...",
                    "Charting emotional territory...",
                    "Sensing the build-up...",
                    "Identifying key moments...",
                    "Scouting for highlights...",
                    "Decoding the rhythm DNA...",
                    "Pinpointing perfection...",
                    "Ooh, found something spicy...",
                    "This part right here? *chef's kiss*...",
                    "My tentacles are getting excited...",
                    "Okay okay okay, we're onto something...",
                    "The algorithms are gossiping about this...",
                    "Running vibe checks...",
                    "Consulting the rhythm oracle...",
                    "The beat just winked at me...",
                    "Doing advanced musical maths...",
                    "Cross-referencing with the groove database...",
                    "Asking the bass for directions...",
                    "Following the dopamine trail...",
                    "The melody is telling secrets...",
                    "Triangulating the best bits...",
                    "My suction cups sense greatness...",
                  ]}
                />
              ) : (
                <EmotionalMixer
                  track={fullTrack}
                  trimRecommendations={trimRecommendations}
                  selectedTrim={selectedTrim}
                  adjustments={adjustments}
                  onTrimSelect={setSelectedTrim}
                  onMoodAdjust={setAdjustments}
                  audioUrl={fullTrack.file_url}
                  wordTimestamps={(fullTrack.context_data?.transcription as Record<string, unknown>)?.word_timestamps as Array<{word: string; start: number; end: number}> || []}
                />
              )}

              {/* Cultural Context Selection */}
              <CulturalSelector
                selectedCulture={culturalContext}
                onSelect={setCulturalContext}
              />

              {/* Brain-Powered Generation Toggle */}
              <div className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 rounded-xl p-4 border border-purple-500/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500">
                      <Brain className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h3 className="font-medium">AI Brain Pipeline</h3>
                      <p className="text-xs text-zinc-400">
                        Uses 3 specialized AI brains for artist matching, visual design, and scene composition
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setUseBrain(!useBrain)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      useBrain ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-zinc-700'
                    }`}
                  >
                    <span
                      className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        useBrain ? 'translate-x-7' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                {useBrain && (
                  <div className="mt-3 pt-3 border-t border-purple-500/20">
                    <div className="flex items-center gap-4 text-xs text-zinc-400">
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-purple-400" />
                        Music Analyst
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-pink-400" />
                        Visual Designer
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-amber-400" />
                        Screenwriter
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Quality Selection */}
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-purple-400" />
                  <h3 className="font-medium">Quality</h3>
                </div>

                <div className="space-y-2">
                  {QUALITY_OPTIONS.map((option) => (
                    <button
                      key={option.tier}
                      onClick={() => setQualityTier(option.tier)}
                      className={`w-full p-4 rounded-lg border transition-all text-left ${
                        qualityTier === option.tier
                          ? 'border-jelly-500 bg-jelly-500/20'
                          : 'border-zinc-700 hover:border-zinc-600'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-medium text-lg">{option.name}</div>
                        <div className="flex items-center gap-3 text-sm">
                          <span className="flex items-center gap-1 text-zinc-400">
                            <Clock className="w-3 h-3" />
                            {option.estimatedTime}
                          </span>
                          <span className={`font-medium ${option.costPerSecond === 0 ? 'text-green-400' : 'text-zinc-300'}`}>
                            {option.costPerSecond === 0 ? 'Free' : `~$${option.costPerSecond.toFixed(3)}/sec`}
                          </span>
                        </div>
                      </div>
                      <div className="text-sm text-zinc-400 mb-2">{option.description}</div>
                      <div className="flex items-center gap-4 text-xs">
                        <span className="flex items-center gap-1 text-zinc-500">
                          <Layers className="w-3 h-3" />
                          {option.providers.join(' → ')}
                        </span>
                        <span className="flex items-center gap-1 text-zinc-500">
                          <Zap className="w-3 h-3" />
                          {option.strategy}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Cost Estimate */}
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-green-400" />
                    <span className="font-medium">Estimated Cost</span>
                  </div>
                  <span className="text-lg font-bold">
                    {estimatedCost() === 0 ? 'Free' : `~$${estimatedCost().toFixed(2)}`}
                  </span>
                </div>
                {selectedTrim && (
                  <p className="text-xs text-zinc-500 mt-2">
                    {selectedTrim.duration}s video with {qualityTier} quality
                  </p>
                )}
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}
                </>
              ) : null}
            </div>
          )}

          {/* Previewing Stage (loading) */}
          {stage === 'previewing' && (
            <div className="py-8">
              <OctopusLoader
                message="Running preview pipeline"
                rotatingMessages={[
                  "Running Brain analysis...",
                  "Composing scenes...",
                  "Routing to video models...",
                  "Building API payloads...",
                  "Calculating costs...",
                  "No API calls being made...",
                  "Checking platform constraints...",
                  "Almost done previewing...",
                ]}
                messageInterval={3000}
              />
            </div>
          )}

          {/* Preview Stage (dry-run report) */}
          {stage === 'preview' && dryRunReport && (
            <DryRunPreview
              report={dryRunReport}
              onProceed={() => {
                setStage('setup');
                // After reviewing preview, user can click Generate
                handleStartGeneration();
              }}
              onBack={() => {
                setStage('setup');
                setDryRunReport(null);
              }}
            />
          )}

          {/* Generating Stage */}
          {stage === 'generating' && (
            <div className="py-8">
              {/* Brain Pipeline Progress (when brain is enabled) */}
              {useBrain && brainPipeline.state !== 'idle' && (
                <div className="mb-6">
                  <BrainPipelineProgress
                    state={brainPipeline.state}
                    brainStatuses={brainPipeline.brainStatuses}
                    currentBrain={brainPipeline.currentBrain}
                    progress={brainPipeline.progress}
                    error={brainPipeline.error}
                    artistMatches={brainPipeline.artistMatches}
                    visualStylePreview={brainPipeline.visualStylePreview}
                    scenePreview={brainPipeline.scenePreview}
                    elapsedSeconds={brainPipeline.elapsedSeconds}
                    onConfirmArtists={(confirmed) => {
                      brainPipeline.sendUserSelection('artist_confirmation', { confirmed });
                    }}
                    onAdjustStyle={() => {
                      // Could open a style adjustment modal
                      console.log('Adjust style requested');
                    }}
                  />
                </div>
              )}

              {/* Timer and Status (when brain is not active or for video generation phase) */}
              {(!useBrain || brainPipeline.state === 'complete') && (
                <div className="text-center mb-6">
                  <div className="inline-flex items-center gap-2 bg-zinc-800 rounded-full px-4 py-2 mb-4">
                    <Clock className="w-4 h-4 text-jelly-400 animate-pulse" />
                    <span className="font-mono text-lg font-bold">
                      {Math.floor(elapsedTime / 60)}:{String(elapsedTime % 60).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="text-sm text-zinc-400 mb-2">{currentStatus}</div>
                </div>
              )}

              {/* Scene Progress */}
              {sceneProgress.length > 0 && (
                <div className="bg-zinc-800/50 rounded-xl p-4 mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Layers className="w-4 h-4 text-purple-400" />
                    <span className="text-sm font-medium">Scene Progress</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {sceneProgress.map((scene) => (
                      <div
                        key={scene.scene_id}
                        className={`p-2 rounded-lg text-center text-xs ${
                          scene.status === 'complete' ? 'bg-green-500/20 text-green-400' :
                          scene.status === 'generating' ? 'bg-jelly-500/20 text-jelly-400 animate-pulse' :
                          scene.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                          'bg-zinc-700/50 text-zinc-500'
                        }`}
                      >
                        <div className="font-medium">Scene {scene.scene_id}</div>
                        {scene.provider && <div className="text-[10px] opacity-70">{scene.provider}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <OctopusLoader
                message="Creating your video"
                rotatingMessages={[
                  "Composing the perfect scenes...",
                  "Painting with pixels...",
                  "Mixing visual magic...",
                  "Feeling the wind in our tentacles...",
                  "Teaching AI to dance...",
                  "Spreading good vibes...",
                  "Syncing to the beat...",
                  "Adding a splash of creativity...",
                  "Rendering dreams into reality...",
                  "Almost there, just vibing...",
                  "Sprinkling some stardust...",
                  "Weaving visual poetry...",
                  "Sculpting moments in time...",
                  "Choreographing pixels...",
                  "Stitching scenes together...",
                  "Polishing the magic...",
                  "Adding the finishing touches...",
                  "Infusing with emotion...",
                  "Bringing your vision to life...",
                  "Making art happen...",
                  "Turning beats into beauty...",
                  "Crafting visual harmony...",
                  "Breathing life into frames...",
                  "Orchestrating visual symphony...",
                  "Summoning creative energy...",
                  "Letting imagination flow...",
                  "Manifesting musical visuals...",
                  "Transforming sound to sight...",
                  "Channeling artistic vibes...",
                  "Building your masterpiece...",
                  "Assembling awesomeness...",
                  "Generating pure fire...",
                  "Creating something special...",
                  "Working our tentacle magic...",
                  "Cooking up visual treats...",
                  "Hold up, this is gonna be good...",
                  "The pixels are cooperating nicely...",
                  "AI is having its main character moment...",
                  "Rendering at the speed of vibes...",
                  "This is the fun part. Trust...",
                  "Making movie magic, one frame at a time...",
                  "The robots are feeling inspired...",
                  "Neurons firing, pixels aligning...",
                  "Currently in the creative zone...",
                  "Art is happening. Please stand by...",
                  "The muses have entered the chat...",
                  "Connecting colors to emotions...",
                  "GPU goes brrrr...",
                  "Turning your track into eye candy...",
                  "Visual buffet incoming...",
                  "Making memories you haven't seen yet...",
                  "The future is rendering...",
                  "Spicy visuals loading...",
                  "Worth the wait, promise...",
                  "Cinema-grade vibes materializing...",
                  "This isn't even my final form...",
                  "Greatness takes a minute...",
                  "Quality content in progress...",
                  "The algorithm is feeling artistic...",
                ]}
                messageInterval={4000}
              />
            </div>
          )}

          {/* Complete Stage — A/B Mode */}
          {stage === 'complete' && abMode && (
            <div className="space-y-6">
              <div className="text-center py-4">
                <h3 className="text-xl font-bold mb-1">A/B Results</h3>
                <p className="text-sm text-zinc-400">Compare Seedance vs fal.ai output</p>
              </div>
              <VideoComparison
                projectId=""
                optionA={optionA}
                optionB={optionB}
                audioUrl={(fullTrack || track).file_url}
                trimSelection={selectedTrim}
                onSelectWinner={handleSelectWinner}
                selectedOption={selectedOption}
              />
            </div>
          )}

          {/* Complete Stage — Standard Mode */}
          {stage === 'complete' && !abMode && (
            <div className="space-y-6">
              {/* Success octopus */}
              <div className="text-center py-6">
                <div className="w-24 h-24 mx-auto mb-4 relative">
                  <svg viewBox="0 0 100 100" className="w-full h-full animate-bounce">
                    <ellipse cx="50" cy="35" rx="25" ry="22" fill="url(#happyGrad)" />
                    <ellipse cx="40" cy="32" rx="5" ry="6" fill="white" />
                    <ellipse cx="60" cy="32" rx="5" ry="6" fill="white" />
                    <circle cx="41" cy="33" r="2.5" fill="#1a1a2e" />
                    <circle cx="61" cy="33" r="2.5" fill="#1a1a2e" />
                    <path d="M40 45 Q50 55 60 45" stroke="#1a1a2e" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                    <path d="M28 55 Q24 70 20 82" stroke="url(#tentGrad)" strokeWidth="6" fill="none" strokeLinecap="round" />
                    <path d="M38 58 Q35 73 32 85" stroke="url(#tentGrad)" strokeWidth="6" fill="none" strokeLinecap="round" />
                    <path d="M50 60 Q50 75 50 88" stroke="url(#tentGrad)" strokeWidth="6" fill="none" strokeLinecap="round" />
                    <path d="M62 58 Q65 73 68 85" stroke="url(#tentGrad)" strokeWidth="6" fill="none" strokeLinecap="round" />
                    <path d="M72 55 Q76 70 80 82" stroke="url(#tentGrad)" strokeWidth="6" fill="none" strokeLinecap="round" />
                    <defs>
                      <linearGradient id="happyGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#e84a8a" />
                        <stop offset="100%" stopColor="#d63384" />
                      </linearGradient>
                      <linearGradient id="tentGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#d63384" />
                        <stop offset="100%" stopColor="#e84a8a" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <Sparkles className="absolute -top-2 -right-2 w-6 h-6 text-yellow-400 animate-pulse" />
                  <Sparkles className="absolute -bottom-1 -left-2 w-5 h-5 text-purple-400 animate-pulse" />
                </div>
                <h3 className="text-xl font-bold">Video Created!</h3>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-jelly-400">
                    {sceneResults.filter((s) => s.status === 'complete').length}
                  </div>
                  <div className="text-xs text-zinc-400">Scenes Created</div>
                </div>
                <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-green-400">
                    ${totalCost.toFixed(2)}
                  </div>
                  <div className="text-xs text-zinc-400">Total Cost</div>
                </div>
                <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-purple-400">
                    {Math.round(processingTime / 1000)}s
                  </div>
                  <div className="text-xs text-zinc-400">Processing Time</div>
                </div>
              </div>

              {/* Classification feedback */}
              {(() => {
                // Extract music_dna from brain production brief or track data
                const brief = brainPipeline.productionBrief?.brief || {};
                const musicDna = brief.music_dna as Record<string, unknown> | undefined;
                const genre = (musicDna?.genre as string) || (fullTrack || track)?.primary_genre || '';
                const moodTags = (musicDna?.mood_tags as string[]) || [];
                const instruments = (musicDna?.instruments as string[]) || [];
                const artists = brainPipeline.artistMatches.length > 0
                  ? brainPipeline.artistMatches.map(a => ({ name: a.artistName }))
                  : ((musicDna?.similar_artists as Array<{ name: string }>) || []);

                return genre ? (
                  <ClassificationFeedback
                    trackContextId={track.id}
                    genre={genre}
                    moodTags={moodTags}
                    similarArtists={artists}
                    instruments={instruments}
                  />
                ) : null;
              })()}

              {/* Video preview with audio sync */}
              {sceneResults.some(s => s.video_url) && (fullTrack || track).file_url ? (
                <VideoPreviewPlayer
                  sceneResults={sceneResults}
                  audioUrl={(fullTrack || track).file_url}
                  trimSelection={selectedTrim}
                  totalDuration={(fullTrack || track).duration_seconds || 180}
                />
              ) : videoUrl ? (
                <div className="bg-zinc-800/50 rounded-xl p-4">
                  <h4 className="font-medium mb-3">Final Video</h4>
                  <video
                    src={videoUrl}
                    controls
                    className="w-full rounded-lg"
                    style={{ maxHeight: '300px' }}
                  />
                </div>
              ) : (
                <div className="bg-zinc-800/50 rounded-xl p-4">
                  <h4 className="font-medium mb-3">Generated Scenes</h4>
                  <div className="space-y-2">
                    {sceneResults.map((scene) => (
                      <div
                        key={scene.scene_id}
                        className="flex items-center justify-between p-2 bg-zinc-900 rounded-lg"
                      >
                        <span className="text-sm">Scene {scene.scene_id}</span>
                        {scene.video_url && (
                          <a
                            href={scene.video_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-jelly-400 hover:text-jelly-300 text-sm flex items-center gap-1"
                          >
                            View <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Error Stage */}
          {stage === 'error' && (
            <div className="py-12 text-center">
              <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Generation Failed</h3>
              <p className="text-zinc-400 mb-6">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-zinc-800">
          {stage === 'setup' && (
            <div className="flex gap-3">
              <Button variant="secondary" className="flex-shrink-0" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="secondary"
                className="flex-1"
                onClick={handlePreview}
                disabled={!selectedTrim || isLoadingTrims || isLoadingTrack || !fullTrack}
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
              <Button
                className="flex-1"
                onClick={handleStartGeneration}
                disabled={!selectedTrim || isLoadingTrims || isLoadingTrack || !fullTrack}
              >
                <Video className="w-4 h-4 mr-2" />
                Generate
              </Button>
              {/* A/B Test button disabled — both pathways run identical pipelines.
                 Re-enable when user-selectable model choice is implemented.
              <Button
                className="flex-1"
                variant="secondary"
                onClick={handleStartABGeneration}
                disabled={!selectedTrim || isLoadingTrims || isLoadingTrack || !fullTrack}
              >
                <GitCompare className="w-4 h-4 mr-2" />
                A/B Test
              </Button>
              */}
            </div>
          )}

          {stage === 'previewing' && (
            <div className="text-center text-sm text-zinc-400">
              Running preview pipeline (no API costs)...
            </div>
          )}

          {stage === 'generating' && (
            <div className="text-center text-sm text-zinc-400">
              This may take a few minutes. Please don't close this window.
            </div>
          )}

          {stage === 'complete' && (
            <div className="flex gap-3">
              <Button variant="secondary" className="flex-1" onClick={handleClose}>
                Close
              </Button>
              {videoUrl && (
                <Button className="flex-1" onClick={() => window.open(videoUrl, '_blank')}>
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              )}
            </div>
          )}

          {stage === 'error' && (
            <div className="flex gap-3">
              <Button variant="secondary" className="flex-1" onClick={handleClose}>
                Close
              </Button>
              <Button className="flex-1" onClick={handleRetry}>
                Try Again
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
